import { logger } from '../utils/logger';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { findAll, get, remove, save, update } from "../services/Service";
import { Equipamiento, EquipamientoFiltro, PaginaEquipamiento } from "../types/Equipamiento";
import { DocumentoEquipamiento, TipoDocumentoOpcion } from "../types/DocumentoEquipamiento";
import { equipamientoFiltroInitial, isLoading, loadDataFiltro, loadPaginaEquipamiento, TAMANIO_PAGINA_INICIAL } from "../context/Equipamiento/EquipamientoReducer";
import { armarParametrosBusqueda } from "../modules/Pages/Equipamiento/parametros";

export const useEquipamiento = () => {
    const BASE_URL = 'equipamiento';
    const { loading, equipamientoList, filtro, paginacion } = useSelector(
        (state: RootState) => state.equipamiento
    );

    const dispatch = useDispatch();

    const buscarEquipamientos = async (filtroBusqueda: EquipamientoFiltro, page: number = 0, size: number = paginacion.size): Promise<PaginaEquipamiento | undefined> => {
        dispatch(isLoading(true));
        try {
            const response = await findAll(`${BASE_URL}/findBy`, armarParametrosBusqueda(filtroBusqueda, page, size));
            dispatch(loadPaginaEquipamiento(response.data));
            return response.data;
        } catch (error) {
            logger.log(error);
        } finally {
            dispatch(isLoading(false));
        }
    }

    const buscarEquipamiento = async (id: string): Promise<Equipamiento> => {
        const response = await get(BASE_URL, id);
        return response.data;
    }

    // Tipos de documento activos, para el combo del modal de tipos de documento.
    const buscarTiposDocumentoActivos = async (): Promise<TipoDocumentoOpcion[]> => {
        try {
            const response = await findAll('tiposDocumentos/findBy', { activo: 'true' });
            return (response.data || []).map((t: any) => ({ tipoDocumentoId: t.id, tipoDocumentoNombre: t.nombre }));
        } catch (error) {
            logger.log(error);
            return [];
        }
    }

    // Baja lógica: el backend deja el equipamiento inactivo. El error se propaga para mostrar su mensaje (400).
    const eliminarEquipamiento = async (id: number) => {
        await remove(BASE_URL, String(id));
    }

    // Alta si el id es 0, edición si no. El error se propaga: la pantalla muestra el mensaje del backend (400).
    const guardarEquipamiento = async (equipamiento: Equipamiento, documentos: DocumentoEquipamiento[]) => {
        const body = {
            id: equipamiento.id,
            nombre: equipamiento.nombre.trim(),
            descripcion: equipamiento.descripcion,
            activo: equipamiento.activo,
            documentos: documentos.map((d) => ({
                tipoDocumentoId: d.tipoDocumentoId,
                obligatorio: d.obligatorio,
                renovable: d.renovable,
                fechaEditable: d.fechaEditable,
                periodo: d.periodo,
            })),
        };
        return equipamiento.id === 0 ? await save(BASE_URL, body) : await update(BASE_URL, body);
    }

    const handleFiltroEquipamiento = (filtroAplicado: EquipamientoFiltro) => {
        dispatch(loadDataFiltro(filtroAplicado));
    }

    return {
        buscarEquipamientos,
        buscarEquipamiento,
        buscarTiposDocumentoActivos,
        guardarEquipamiento,
        eliminarEquipamiento,
        handleFiltroEquipamiento,
        equipamientoFiltroInitial,
        tamanioPaginaInicial: TAMANIO_PAGINA_INICIAL,
        equipamientoList,
        filtro,
        paginacion,
        loading,
    }
}
